import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CaretRight, ClockCounterClockwise } from '@phosphor-icons/react';

import { cardsAPI } from '../api/modules/cards.module';
import { routes } from '../routes/routes';
import { mediaUrl } from '../utils/common';
import useDebounce from './useDebounce';

/**
 * The nav search, wired once for the whole buying side.
 *
 * WHY THIS IS ONE HOOK AND NOT A PROP EACH PAGE PASSES
 * ----------------------------------------------------
 * Before this, exactly one page (`/marketplace`) handed `MarketShell` a
 * `search` object, and it wired the box to that page's own grid filter. So
 * the same control in the same 560px of chrome did something on one route and
 * nothing on the other nine — and on the one route where it worked, it filtered
 * the grid live, which is why the typeahead was removed during the conversion:
 * a dropdown of card names sat on top of a grid already showing those cards.
 *
 * The box is now a GLOBAL search on every MarketShell page. Typing opens a
 * typeahead; Enter goes to `/search?q=`, the results page, which is the
 * marketplace browse surface with the query applied and its facets intact.
 * That splits the two jobs the old design had collapsed into one: BROWSE is
 * `/marketplace` and its filter rail, SEARCH is a query and a results page.
 *
 * WHAT THE PANEL SHOWS, AND WHY IN THAT ORDER
 *   Prompt    the not-yet-searched state, only while the box is empty. It says
 *             what the box is for and nothing else.
 *   Recent    what you searched before, from localStorage. Only when the box
 *             is empty — once you are typing, your history is not the answer.
 *   Cards     the actual matches, with art, capped at six. The cap is the
 *             point: this is a shortcut to ONE card, not a second results
 *             page rendered on top of the first.
 *   See all   the one destination a typed query also means, at the bottom of a
 *             list of matches.
 *
 * WHAT THE PANEL NO LONGER SHOWS. An empty box used to carry a "Go to" list —
 * Browse the marketplace / Browse by game and set / Your orders. All three are
 * already links in the nav bar this box sits inside, so the panel spent its
 * whole height repeating the chrome around it, and someone who clicked SEARCH
 * had to read past three non-answers to reach their own history. The prompt
 * above replaces it.
 */

const RECENT_KEY = 'sleevr_recent_searches';
const RECENT_MAX = 5;
const SUGGESTION_MAX = 6;

export function readRecentSearches() {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((entry) => typeof entry === 'string') : [];
  } catch {
    // A blocked or corrupt localStorage costs the history, never the search.
    return [];
  }
}

/**
 * Drop one entry.
 *
 * The whole history is a frontend cache — `localStorage`, five strings, never
 * sent anywhere — so removing one is a local write and nothing else. There is
 * no server call to make and no failure to report: a blocked `localStorage`
 * already costs the history on read, and the same `catch` covers the write.
 */
function removeRecentSearch(query) {
  const next = readRecentSearches().filter((entry) => entry !== query);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* see above */
  }
  return next;
}

function writeRecentSearch(query) {
  const trimmed = String(query || '').trim();
  if (!trimmed) return readRecentSearches();

  const next = [trimmed, ...readRecentSearches().filter((entry) => entry !== trimmed)].slice(
    0,
    RECENT_MAX,
  );
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* see above */
  }
  return next;
}

/**
 * What an untouched box says.
 *
 * A module constant, not an object literal in the memo, so its identity never
 * changes — `SearchBox` reopens its panel when what it is given changes
 * identity, and a fresh object each render would fight every dismissal.
 *
 * The copy names the one thing this box is for, then the one key that finishes
 * the job. It does NOT list what is searchable — the placeholder in the field
 * three pixels above it already says "cards, sets, sealed product", and it
 * would be the third time in one panel — and it does not promise sellers or
 * stores, because `getLookUpCards` behind this only matches the card catalogue.
 */
const EMPTY_STATE = {
  title: 'Search for the card you’re looking for',
  body: 'Start typing. Enter shows every match.',
};

export function useGlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // On the results page the box shows the query it is showing results for, so
  // refining is editing what is there rather than retyping it.
  const queryParam = location.pathname === routes.SEARCH ? searchParams.get('q') || '' : '';
  const [value, setValue] = useState(queryParam);
  useEffect(() => {
    setValue(queryParam);
  }, [queryParam]);

  const [recent, setRecent] = useState(readRecentSearches);
  const [matches, setMatches] = useState([]);

  // 250, not the grid's 500: a dropdown that lands after you have stopped
  // typing is a dropdown you have already given up on.
  const debounced = useDebounce(value.trim(), 250);
  const seqRef = useRef(0);

  useEffect(() => {
    // Nothing to suggest for one character, and nothing to suggest for the
    // query whose results are already on screen — on `/search?q=lotus` the box
    // shows "lotus", and a typeahead offering six of the results underneath it
    // is the exact duplication that got the old dropdown deleted.
    if (debounced.length < 2 || debounced === queryParam) {
      setMatches([]);
      return undefined;
    }

    const seq = ++seqRef.current;
    let live = true;

    cardsAPI
      .getLookUpCards({ search: debounced, page: 1, page_size: SUGGESTION_MAX })
      .then((response) => {
        // Same race guard the grid fetch uses: a slower earlier keystroke must
        // never overwrite the answer to a later one.
        if (!live || seq !== seqRef.current) return;
        setMatches((response?.data || []).slice(0, SUGGESTION_MAX));
      })
      .catch(() => {
        if (!live || seq !== seqRef.current) return;
        // A failed typeahead shows no typeahead. It never shows an error: the
        // box still works, and Enter still runs the search.
        setMatches([]);
      });

    return () => {
      live = false;
    };
  }, [debounced]);

  const submit = useCallback(
    (raw) => {
      const query = String(raw ?? value).trim();
      if (!query) return;
      setRecent(writeRecentSearch(query));
      navigate(`${routes.SEARCH}?q=${encodeURIComponent(query)}`);
    },
    [navigate, value],
  );

  const typing = value.trim().length > 0;

  const sections = useMemo(() => {
    if (!typing) {
      // History and nothing else. An empty `items` array is dropped by
      // `SearchBox`, so a user with no history gets the prompt on its own
      // rather than a heading over a void.
      return [
        {
          id: 'recent',
          title: 'Recent searches',
          items: recent.map((entry) => ({
            id: entry,
            label: entry,
            icon: ClockCounterClockwise,
            query: entry,
            /* The only removable row in the panel. A matched card is the
               catalogue and a "see all" is a destination — neither is yours to
               delete. Your own history is. */
            removable: true,
          })),
        },
      ];
    }

    return [
      {
        id: 'cards',
        title: 'Cards',
        items: matches.map((card) => ({
          id: card.id,
          label: card.product_name,
          sub: card.catalog_group,
          thumb: card.image_url ? mediaUrl(card.image_url) : undefined,
          to: `${routes.MARKETPLACE}/card/${card.id}`,
        })),
      },
      {
        id: 'all',
        title: null,
        items: [
          {
            id: 'see-all',
            label: `See all results for “${value.trim()}”`,
            icon: CaretRight,
            query: value.trim(),
          },
        ],
      },
    ];
  }, [typing, recent, matches, value]);

  const remove = useCallback((item) => {
    if (!item || !item.query) return;
    setRecent(removeRecentSearch(item.query));
  }, []);

  const select = useCallback(
    (item) => {
      // Two kinds of row now that the nav links are gone: anything carrying a
      // `query` (a remembered search, "see all results for …") RUNS that
      // search, anything carrying a `to` (a matched card) navigates to it.
      if (item.query) {
        setValue(item.query);
        submit(item.query);
        return;
      }
      if (item.to) navigate(item.to);
    },
    [navigate, submit],
  );

  return {
    value,
    onChange: setValue,
    onSubmit: submit,
    sections,
    // Only while the box is empty. Once there is a query the panel's job is to
    // answer it, and "search for the card you're looking for" over a list of
    // matches would be instructions for something already done.
    emptyState: typing ? null : EMPTY_STATE,
    onSelectSuggestion: select,
    /* Only rows marked `removable` render the control that calls this, so a
       panel of card matches never grows delete buttons. */
    onRemoveSuggestion: remove,
    openOnFocus: true,
    placeholder: 'Search cards, sets, sealed product',
  };
}

export default useGlobalSearch;
